import React from "react";
import { UI_BODY_SM, colors2 } from "oolib";
import { UI_BODY_SM_DF } from "oolib/dist/v2/components/Typo2";
import { BarChart } from "@/components/dataviz/BarChart";
import { buildAnnoValuePath, buildAnnoTooltipLabelsPath } from "./utils/buildAnnoBarPaths";

/**
 * The prevalence sections: one titled section per group, each with a bar
 * chart of that group's rows. Shared by the Annotation Explorer's prevalence
 * panel and the Annotation Counts widget (reports / Overview / DataPivot) so
 * the two can never drift on layout, sorting or the header's count + share.
 *
 * A group is `{ subThemeId, subThemeDisplay, total, percentage, tags[] }` —
 * the shape both groupers emit (groupAnnotationDataBySubTheme for the
 * taxonomy, groupTopicDataByTopic for the clustering tree). `percentage` is
 * always the BE-stamped share of all annotations in scope, so section shares
 * deliberately do not sum to 100: one annotation sits under several groups
 * at once.
 *
 * @param groups              the sections to render, in order; a group's
 *                            `percentage` is optional — the header shows
 *                            only the count without it
 * @param colorIdxMap         group id → BarChart colour slot
 * @param onBarClick          (barData, group) => void
 * @param highlightValue      the bar `value` to border — the applied filter
 * @param showCount           print each bar's count on it
 * @param renderSectionAction (group) => node — the header's right-hand
 *                            control; the explorer passes its colour-code
 *                            paint bucket, the widget passes nothing
 * @param barColorByValue     spotlight map for colour-coded bars
 * @param summarizeAfterIdx   bars past this index fold into one "Others" bar;
 *                            Infinity (the default) shows every bar
 * @param sectionPadding      horizontal padding for the header text, matched
 *                            to the host's own gutter
 */
export const PrevalenceSections = ({
  groups,
  colorIdxMap = {},
  onBarClick,
  renderSectionAction,
  barColorByValue,
  breakdownDisplayType,
  summarizeAfterIdx = Infinity,
  highlightValue,
  showCount = false,
  sectionPadding = "0 1.4rem",
  emptyMessage = "No data for this selection.",
}) => {
  if (!groups?.length) {
    return (
      <div style={{ padding: "1.4rem" }}>
        <UI_BODY_SM style={{ color: colors2.grey60 }}>{emptyMessage}</UI_BODY_SM>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", width: "100%" }}>
      {groups.map((group) => {
        const action = renderSectionAction?.(group);
        return (
          <div key={group.subThemeId}>
            <div
              style={{
                paddingBottom: "0.8rem",
                paddingTop: "0.8rem",
                borderTop: `1px solid ${colors2.grey10}`,
              }}
            >
              <div
                style={{
                  padding: sectionPadding,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "0.8rem",
                }}
              >
                <UI_BODY_SM_DF>
                  {group.subThemeDisplay}{" "}
                  <UI_BODY_SM_DF style={{ display: "inline", color: colors2.grey60 }}>
                    · {Number.isFinite(group.percentage) ? `${Math.round(group.percentage)}% (${group.total})` : group.total}
                  </UI_BODY_SM_DF>
                </UI_BODY_SM_DF>
                {action}
              </div>
            </div>
            <BarChart
              data={group.tags}
              valuePath={buildAnnoValuePath(group.tags)}
              tooltipLabelsPath={buildAnnoTooltipLabelsPath(group.tags)}
              labelPath="display"
              colorIdx={colorIdxMap[group.subThemeId] ?? 0}
              breakdownDisplayType={breakdownDisplayType}
              summarizeAfterIdx={summarizeAfterIdx}
              onClick={(bar) => onBarClick?.(bar, group)}
              barColorByValue={barColorByValue}
              highlightValue={highlightValue}
              showCount={showCount}
            />
          </div>
        );
      })}
    </div>
  );
};
